import { useQueryClient } from "@tanstack/react-query"
import { useEffect, useMemo, useState } from "react"

import { ListItemStack } from "@molecules"
import GenericForm from "@molecules/Form/GenericForm/GenericForm"

import { Settings } from "@templates/index"

import {
    InventoryNotificationTopics,
    NotificationMethods,
} from "@pages/Settings/SettingsNotifications/SettingsNotification.types"
import {
    notificationPreferenceQueryKey,
    useNotificationPreferencesQuery,
} from "@pages/Settings/SettingsNotifications/SettingsNotifications.store"
import NotificationProperty from "@pages/Settings/SettingsNotifications/components/NotificationProperty/NotificationProperty"

import { NOTIFICATION_ENDPOINTS } from "@endpoints/notification"

export default function InventoryNotificationsForm() {
    const [shouldEnableAllEmailNotifications, setShouldEnableAllEmailNotifications] = useState<boolean>(false)
    const [shouldDisableAllEmailNotifications, setShouldDisableAllEmailNotifications] = useState<boolean>(false)
    const [shouldEnableAllSmsNotifications, setShouldEnableAllSmsNotifications] = useState<boolean>(false)
    const [shouldDisableAllSmsNotifications, setShouldDisableAllSmsNotifications] = useState<boolean>(false)
    const [shouldSetDefaultFormData, setShouldSetDefaultFormData] = useState<boolean>(false)

    const { topicPreference: inventoryNotificationPreference, isLoading: isLoadingData } =
        useNotificationPreferencesQuery<InventoryNotificationTopics>("inventory")

    const queryClient = useQueryClient()

    useEffect(() => {
        if (!isLoadingData && !!inventoryNotificationPreference) {
            setShouldSetDefaultFormData(true)
        }
    }, [isLoadingData])

    const modifyFieldset = (fieldset: InventoryNotificationTopics, method: NotificationMethods, value: boolean) => {
        return Object.keys(fieldset).reduce((acc, key) => {
            const notificationKey = key as keyof InventoryNotificationTopics
            if (notificationKey.endsWith(`_${method}`)) {
                acc[notificationKey] = value
            }
            return acc
        }, fieldset)
    }

    const formValues = useMemo(() => {
        if (shouldEnableAllEmailNotifications) {
            return modifyFieldset(inventoryNotificationPreference, "email", true)
        } else if (shouldEnableAllSmsNotifications) {
            return modifyFieldset(inventoryNotificationPreference, "sms", true)
        } else if (shouldDisableAllEmailNotifications) {
            return modifyFieldset(inventoryNotificationPreference, "email", false)
        } else if (shouldDisableAllSmsNotifications) {
            return modifyFieldset(inventoryNotificationPreference, "sms", false)
        } else if (shouldSetDefaultFormData) {
            return inventoryNotificationPreference
        } else {
            return undefined
        }
    }, [
        shouldSetDefaultFormData,
        shouldEnableAllEmailNotifications,
        shouldDisableAllEmailNotifications,
        shouldEnableAllSmsNotifications,
        shouldDisableAllSmsNotifications,
    ])

    const mutationTriggerPayload = useMemo(() => {
        if (shouldEnableAllEmailNotifications) {
            return modifyFieldset(inventoryNotificationPreference, "email", true)
        } else if (shouldEnableAllSmsNotifications) {
            return modifyFieldset(inventoryNotificationPreference, "sms", true)
        } else if (shouldDisableAllEmailNotifications) {
            return modifyFieldset(inventoryNotificationPreference, "email", false)
        } else if (shouldDisableAllSmsNotifications) {
            return modifyFieldset(inventoryNotificationPreference, "sms", false)
        } else {
            return undefined
        }
    }, [
        shouldEnableAllEmailNotifications,
        shouldDisableAllEmailNotifications,
        shouldEnableAllSmsNotifications,
        shouldDisableAllSmsNotifications,
    ])

    const resetNotificationStates = () => {
        setShouldEnableAllEmailNotifications(false)
        setShouldDisableAllEmailNotifications(false)
        setShouldEnableAllSmsNotifications(false)
        setShouldDisableAllSmsNotifications(false)
    }

    const onMutationSuccess = () => {
        void queryClient.invalidateQueries({ queryKey: notificationPreferenceQueryKey })
    }

    const onAfterFormValuesSet = () => {
        resetNotificationStates()
        setShouldSetDefaultFormData(false)
    }

    return (
        <GenericForm<InventoryNotificationTopics, InventoryNotificationTopics, Error, InventoryNotificationTopics>
            formConfig={{
                defaultValues: undefined,
            }}
            mutationConfig={{
                genericErrorMessage: "Could not update notification preference",
                genericSuccessMessage: "Notification preference updated",
                method: "PATCH",
                endpoint: NOTIFICATION_ENDPOINTS.UPDATE_NOTIFICATION_PREFERENCES,
            }}
            onMutationSuccess={onMutationSuccess}
            mutationTriggerPayload={mutationTriggerPayload}
            formValues={formValues}
            onAfterFormValuesSet={onAfterFormValuesSet}
            isAutoSave={true}
        >
            {() => {
                return (
                    <Settings.NotificationConfigPage.Wrapper>
                        <ListItemStack>
                            <NotificationProperty<InventoryNotificationTopics>
                                label="Low inventory digest"
                                supportText="Get a daily summary of all parts that have low or depleted inventory."
                                emailCheckboxName="enable_inventory_notification_low_inventory_email"
                                smsCheckboxName="enable_inventory_notification_low_inventory_sms"
                                isLoading={isLoadingData}
                            />
                        </ListItemStack>
                    </Settings.NotificationConfigPage.Wrapper>
                )
            }}
        </GenericForm>
    )
}
